/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Link from "@mui/joy/Link";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Tooltip from "@mui/joy/Tooltip";
import Divider from "@mui/joy/Divider";
import Chip from "@mui/joy/Chip";

import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useNavigate } from "react-router-dom";
import MultiSelectCheckboxDropdown from "./MultiSelectCheckdownDropbox"; // Ensure path is correct
import { invoiceStatusOptionsVendor, redirect_url_for_download } from "../utils/constants"; // Ensure path is correct
import { OrderTableProps, InvoiceEntry, CombinedSearchArgs, CombinedSearchInvoiceEntry } from "../utils/types"; // Ensure types are imported
import { useGetInvoiceEntriesQuery, useSearchInvoicesQuery, useSoftDeleteInvoiceMutation } from "../services/invoices/InvoiceApiSlice";
import { displayOptional, downloadBase64File, extractInvoiceNumberWithRegex, formatDateTable } from "../utils/utils";

import { toast } from "react-toastify";
import { useGetFileContantMutation } from "../services/vendor/VendorApi";

type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<DataRow extends Record<string, any>, Key extends keyof DataRow>(order: Order, orderBy: Key): (a: DataRow, b: DataRow) => number {
  return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// const formatDate = (dateString: string | null | undefined): string => {
//   if (!dateString) return "N/A";
//   try {
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return dateString; // Return original if invalid
//     return date.toLocaleDateString(undefined, {
//       year: "numeric",
//       month: "short",
//       day: "numeric",
//     });
//   } catch (e) {
//     return dateString; // Return original on error
//   }
// };

const tableHeaders: Array<{
  id: keyof InvoiceEntry | "action" | "dcument_no" | "bank_utr";
  label: string;
  minWidth: number;
  numeric?: boolean;
  disableSort?: boolean;
}> = [
    { id: "bts_no", label: "BTS No.", minWidth: 110 },
    { id: "bts_entry_date", label: "Entry Date", minWidth: 100 },
    // { id: "vendor_name", label: "Vendor", minWidth: 150 },
    { id: "po_contract", label: "PO/Contract", minWidth: 110 },
    { id: "bill_No", label: "Invoice No.", minWidth: 100 },
    { id: "bill_date", label: "Invoice Date", minWidth: 110 },
    { id: "tax_amount", label: "Tax Amt.", minWidth: 90, numeric: true },
    { id: "bill_amount", label: "Invoice Amt.", minWidth: 120, numeric: true },
    // { id: "remarks", label: "Remarks", minWidth: 120, numeric: true },
    { id: "dcument_no", label: "Payment Advice", minWidth: 120, numeric: true },
    { id: "bank_utr", label: "UTR No.", minWidth: 120, numeric: true },
    { id: "status", label: "Status", minWidth: 120 },
    { id: "action", label: "Action", minWidth: 120, disableSort: true }, // Adjusted minWidth
  ];

const ITEMS_PER_PAGE = 10;

export default function VendorOrderTable({ invoiceEntries: initialInvoiceEntries }: OrderTableProps) {

  const navigate = useNavigate();
  const [openFilterModal, setOpenFilterModal] = React.useState(false);
  const [softDeleteInvoice, { isLoading: deleteInprogress, isSuccess: deleteSuccess, isError: errorDeleting, error, data: deleteResponse }] = useSoftDeleteInvoiceMutation();

  const [serverPage, setServerPage] = React.useState(1);
  const [clientPage, setClientPage] = React.useState(1);
  const [getFileContant] = useGetFileContantMutation();

  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof InvoiceEntry | keyof CombinedSearchInvoiceEntry>("bts_entry_date");

  const [filterInputs, setFilterInputs] = React.useState<Omit<CombinedSearchArgs, "page" | "orderBy" | "order">>({
    bts_no: "",
    po_contract: "",
    invoice_status: [],
  });
  const [debouncedFilterInputs, setDebouncedFilterInputs] = React.useState(filterInputs);

  const [deleteModalId, setDeleteModalId] = React.useState<number | string>();

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterInputs(filterInputs);
      setClientPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [filterInputs]);

  const searchApiArgs = React.useMemo<CombinedSearchArgs>(
    () => ({
      ...debouncedFilterInputs,
      orderBy: orderBy as string,
      order,
    }),
    [debouncedFilterInputs, orderBy, order]
  );

  const listApiArgs = React.useMemo(
    () => ({
      // Not directly used for query, but kept for consistency
      page: serverPage,
      orderBy: orderBy as string,
      order,
    }),
    [serverPage, orderBy, order]
  );

  const isSearchActive = React.useMemo(
    () => !!(debouncedFilterInputs.bts_no || debouncedFilterInputs.po_contract || (debouncedFilterInputs.invoice_status && debouncedFilterInputs.invoice_status.length > 0)),
    [debouncedFilterInputs]
  );

  const {
    data: listData,
    isLoading: isLoadingList,
    isError: isErrorList,
    isFetching: isFetchingList,
    refetch: refetchInvoices,
  } = useGetInvoiceEntriesQuery(
    // Pass sorting options if your API supports it for the list view
    // For now, assuming serverPage is the primary argument for listData fetching
    // and sorting is applied after or handled by the API based on listApiArgs if integrated
    serverPage // Assuming useGetInvoiceEntriesQuery accepts these
  );

  React.useEffect(() => {
    refetchInvoices();
  }, []);

  const getInvoiceIdFromState = (locationState: any): string | null => {
    const bts_no = locationState?.invoiceData?.bts_no;
    if (typeof bts_no === "string" && bts_no.includes("-")) {
      return bts_no.split("-")[0];
    }
    return null;
  };

  const {
    data: searchResultsUnsorted,
    isLoading: isLoadingSearch,
    isFetching: isFetchingSearch,
    isError: isErrorSearch,
  } = useSearchInvoicesQuery(searchApiArgs, { skip: !isSearchActive });

  const isLoading = isSearchActive ? isLoadingSearch || isFetchingSearch : isLoadingList || isFetchingList;
  const isError = isSearchActive ? isErrorSearch : isErrorList;

  const searchResults = React.useMemo(() => {
    if (!searchResultsUnsorted) return [];
    return stableSort(searchResultsUnsorted as any[], getComparator(order, orderBy as any)) as CombinedSearchInvoiceEntry[];
  }, [searchResultsUnsorted, order, orderBy]);

  let displayedInvoices: (InvoiceEntry | CombinedSearchInvoiceEntry)[] = [];
  let currentPaginationData: {
    page: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
    total_items: number;
  } | null = null;

  if (isSearchActive) {
    if (searchResults && searchResults.length > 0) {
      const totalItems = searchResults.length;
      const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
      const startIndex = (clientPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      displayedInvoices = searchResults.slice(startIndex, endIndex);

      currentPaginationData = {
        page: clientPage,
        total_pages: totalPages,
        has_prev: clientPage > 1,
        has_next: clientPage < totalPages,
        total_items: totalItems,
      };
    } else {
      displayedInvoices = [];
    }
  } else {
    displayedInvoices = listData?.data || [];
    if (listData?.pagination) {
      currentPaginationData = listData.pagination;
    }
  }

  const handleSort = (property: keyof InvoiceEntry | keyof CombinedSearchInvoiceEntry) => {
    const header = tableHeaders.find((h) => h.id === property);
    if (header?.disableSort) return;

    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setServerPage(1);
    setClientPage(1);
  };


  const handleView = (invoiceId: number | string) => {
    const invoiceToView = displayedInvoices.find((inv) => (inv as InvoiceEntry).id === invoiceId || (inv as CombinedSearchInvoiceEntry).bts_no === invoiceId);
    const idToNavigate = (invoiceToView as InvoiceEntry)?.id || (invoiceToView as CombinedSearchInvoiceEntry)?.bts_no;
    navigate(`/view-invoice-entry/${idToNavigate}`, {
      state: { invoiceData: invoiceToView, isReadOnly: true },
    });
  };

  const handleEdit = (invoiceId: number | string) => {

    const invoiceToEdit = displayedInvoices.find((inv) => (inv as InvoiceEntry).id === invoiceId || (inv as CombinedSearchInvoiceEntry).bts_no === invoiceId);
    const idToNavigate = (invoiceToEdit as InvoiceEntry)?.id || (invoiceToEdit as CombinedSearchInvoiceEntry)?.bts_no;
    navigate(`/edit-entry/${invoiceId}`, {
      state: { invoiceData: invoiceToEdit, isReadOnly: false },
    });
  };

  const handleDelete = async (invoiceId: number | string) => {
    if (!invoiceId) {
      console.error("No invoice ID provided to delete.");
      return;
    }
    try {
      const response = await softDeleteInvoice({
        invoice_id: String(invoiceId),
      }).unwrap();
      // const response = await softDeleteInvoice({
      //   invoice_id: Number(extractInvoiceNumberWithRegex(invoiceId)),
      // }).unwrap();

      setDeleteModalId(null);
      refetchInvoices();
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
      setDeleteModalId(null);
      console.error("Failed to delete invoice:", err);
    }
  };

  const handleFilterChange = (setter: (value: any) => void, value: any) => {
    setter(value);
    setServerPage(1);
  };

  const handleBtsNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange((val) => setFilterInputs((prev) => ({ ...prev, bts_no: val })), event.target.value);
  };

  const handlePoContractChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange((val) => setFilterInputs((prev) => ({ ...prev, po_contract: val })), event.target.value);
  };

  const handleInvoiceStatusChange = (selectedStatusValues: string[]) => {
    handleFilterChange((val) => setFilterInputs((prevArgs) => ({ ...prevArgs, invoice_status: val })), selectedStatusValues);
  };

  const handleApplyFiltersFromModal = () => {
    setOpenFilterModal(false);
  };

  const handlePageChange = (newPage: number) => {
    if (isSearchActive) {
      setClientPage(newPage);
    } else {
      setServerPage(newPage);
    }
  };

  const onHandleDownloadDocumentFile = async (docNumber: any, year: any) => {

    try {
      const response: any = await getFileContant("doc_num=" + docNumber + "&fiscal_year=" + year);

      if (response?.data?.success) {
        var fileData = response?.data?.message;
        downloadBase64File(fileData?.FileContent, fileData?.DocNo + "-" + fileData?.Fyear, fileData?.MimeType);
      } else {
        toast.error(response?.error?.data?.message);
      }
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
    }
  }
  if (isLoading && displayedInvoices.length === 0 && !isFetchingList && !isFetchingSearch) {
    return <Typography sx={{ p: 2, textAlign: "center" }}>Loading initial data...</Typography>;
  }

  if (isError) {
    return <Typography sx={{ p: 2, textAlign: "center", color: "danger.500" }}>Error loading invoice data.</Typography>;
  }

  const renderFilters = (isMobile: boolean) => (
    <>
      <FormControl size="sm" sx={isMobile ? { mt: 1 } : { flexGrow: 1 }}>
        <FormLabel>BTS No.</FormLabel>
        <Input placeholder="Search BTS No." startDecorator={<SearchIcon />} value={filterInputs.bts_no} onChange={handleBtsNoChange} />
      </FormControl>
      <FormControl size="sm" sx={isMobile ? { mt: 1 } : { flexGrow: 1 }}>
        <FormLabel>Purchase Order/Contract</FormLabel>
        <Input placeholder="Search PO/Contract" startDecorator={<SearchIcon />} value={filterInputs.po_contract} onChange={handlePoContractChange} />
      </FormControl>
      <Box sx={isMobile ? { mt: 1 } : { flexGrow: 1, minWidth: { md: "200px" } }}>
        <MultiSelectCheckboxDropdown
          label="Invoice Status"
          options={invoiceStatusOptionsVendor}
          selectedValues={filterInputs.invoice_status || []}
          onChange={handleInvoiceStatusChange}
          placeholder="Filter by status"
          size="sm"
        />
      </Box>
    </>
  );

  return (
    <React.Fragment>
      <Sheet
        className="SearchAndFilters-mobile"
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "flex-end",
          p: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button variant="outlined" color="neutral" onClick={() => setOpenFilterModal(true)}>
          Filters
        </Button>
      </Sheet>
      <Modal open={openFilterModal} onClose={() => setOpenFilterModal(false)}>
        <ModalDialog aria-labelledby="filter-modal" layout="center" sx={{ p: 2, width: "calc(100% - 32px)", maxWidth: 400 }}>
          <ModalClose />
          <Typography id="filter-modal" level="h4">
            Filters
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Sheet sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {renderFilters(true)}
            <Button color="primary" onClick={handleApplyFiltersFromModal} sx={{ mt: 1 }}>
              Apply Filters
            </Button>
          </Sheet>
        </ModalDialog>
      </Modal>
      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          flexDirection: "row",
          width: "100%",
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": { minWidth: { xs: "120px", md: "160px" } },
        }}
      >
        {renderFilters(false)}
      </Box>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 300,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground": "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
          }}
        >
          <thead>
            <tr>
              {tableHeaders.map((headCell) => (
                <th
                  key={headCell.id}
                  style={{
                    width: headCell.minWidth,
                    minWidth: headCell.minWidth,
                    padding: "12px 6px",
                    textAlign: "center",
                  }}
                >
                  {!headCell.disableSort ? (
                    <Link
                      component="button"
                      onClick={() => handleSort(headCell.id as keyof InvoiceEntry | keyof CombinedSearchInvoiceEntry)}
                      fontWeight="lg"
                      endDecorator={
                        orderBy === headCell.id ? (
                          <ArrowDropDownIcon
                            sx={{
                              opacity: 1,
                              transform: order === "desc" ? "rotate(0deg)" : "rotate(180deg)",
                            }}
                          />
                        ) : (
                          <ArrowDropDownIcon sx={{ opacity: 0 }} />
                        )
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        "& svg": {
                          transition: "0.2s",
                          transformOrigin: "center center",
                        },
                        color: "text.primary",
                        "&:hover": {
                          "& svg": { opacity: 1, color: "primary.500" },
                        },
                      }}
                      underline="none"
                    >
                      {headCell.label}
                    </Link>
                  ) : (
                    <Typography fontWeight="lg" level="body-sm">
                      {headCell.label}
                    </Typography>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && displayedInvoices.length === 0 ? (
              <tr>
                <td colSpan={tableHeaders.length} style={{ textAlign: "center", padding: "16px" }}>
                  <Typography>Loading data...</Typography>
                </td>
              </tr>
            ) : !isLoading && displayedInvoices.length === 0 ? (
              <tr>
                <td colSpan={tableHeaders.length} style={{ textAlign: "center", padding: "16px" }}>
                  <Typography>
                    No bills found
                    {isSearchActive ? " matching your filters." : "."}
                  </Typography>
                </td>
              </tr>
            ) : (
              displayedInvoices.map((invoice: any) => (
                <tr key={invoice.id || invoice.bts_no}>
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">{displayOptional(invoice?.bts_no)}</Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">{formatDateTable(invoice?.bts_entry_date)}</Typography>
                  </td>

                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs" className="elliplses-text" title={displayOptional(invoice?.po_contract || invoice.po_no)}>
                      {displayOptional(invoice?.po_contract || invoice.po_no)}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">{displayOptional(invoice?.bill_no || "")}</Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">{formatDateTable(invoice.bill_date)}</Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">{displayOptional(invoice.tax_amount)}</Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">{displayOptional(invoice.bill_amount)}</Typography>
                  </td>
                  {/* <td style={{ textAlign: "center" }}>
                    <Tooltip title={invoice.remarks || ""} placement="top" arrow>
                      <Typography level="body-xs" noWrap>
                        {displayOptional(invoice.remarks)}
                      </Typography>
                    </Tooltip>
                  </td> */}
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">
                      {invoice.docNumber === null ? (
                        "NA"
                      ) : (
                        <span
                          className="download-docNumber"
                          onClick={() => onHandleDownloadDocumentFile(invoice.docNumber, getFiscalYearCode(invoice?.paid_on))}
                        >
                          {invoice.docNumber}
                        </span>
                      )}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Typography level="body-xs">{invoice?.bank_utr || "NA"}</Typography>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={
                        invoice.status?.toLowerCase().includes("pending")
                          ? "warning"
                          : invoice.status?.toLowerCase().includes("parked") || invoice.status?.toLowerCase().includes("submitted")
                            ? "primary"
                            : invoice.status?.toLowerCase().includes("approved") ||
                              invoice.status?.toLowerCase().includes("accepted") ||
                              invoice.status?.toLowerCase().includes("paid")
                              ? "success"
                              : invoice.status?.toLowerCase().includes("rejected") || invoice.status?.toLowerCase().includes("not parked")
                                ? "danger"
                                : "neutral"
                      }
                    >
                      {displayOptional(invoice.status)}
                    </Chip>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton size="sm" color="primary" variant="plain" onClick={() => handleView(invoice.id || invoice.bts_no)} aria-label="View invoice">
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        color="neutral"
                        variant="plain"
                        disabled={!invoice.status?.toLowerCase().includes("saved_as_draft")}
                        onClick={() => handleEdit(invoice.id || invoice.bts_no)}
                        aria-label="Edit invoice"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        color="danger"
                        variant="plain"
                        disabled={!invoice.status?.toLowerCase().includes("saved_as_draft")}
                        onClick={() =>
                          // handleDelete(invoice.id || invoice.bts_no)
                          setDeleteModalId(invoice.id || invoice.bts_no)
                        }
                        aria-label="Delete invoice"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      {currentPaginationData && currentPaginationData.total_pages > 1 && (
        <Box
          className="Pagination-laptopUp"
          sx={{
            pt: 0,
            gap: 1,
            [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
            display: { xs: "none", sm: "flex" },
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            startDecorator={<KeyboardArrowLeftIcon />}
            onClick={() => handlePageChange(currentPaginationData?.page - 1)}
            disabled={!currentPaginationData.has_prev || isLoading}
          >
            Previous
          </Button>

          <Typography level="body-sm" sx={{ mx: 1 }} >
            Page {currentPaginationData?.page} of {currentPaginationData.total_pages}
          </Typography>

          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            endDecorator={<KeyboardArrowRightIcon />}
            onClick={() => handlePageChange(currentPaginationData?.page + 1)}
            disabled={!currentPaginationData.has_next || isLoading}
          >
            Next
          </Button>
        </Box>
      )}
      <Modal
        open={!!deleteModalId}
        onClose={() => (!deleteInprogress ? setDeleteModalId(null) : null)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            zIndex: 1300,
            bgcolor: "background.surface",
            border: "1px solid",
            borderColor: "neutral.outlinedBorder",
            boxShadow: "lg",
            borderRadius: "md",
            p: 3,
          }}
        >
          <Typography level="h4">Confirm Deletion</Typography>
          <Typography sx={{ mt: 1 }}>Are you sure you want to delete this bill? This action cannot be undone.</Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              mt: 3,
            }}
          >
            <Button variant="outlined" color="neutral" onClick={() => setDeleteModalId(null)} disabled={deleteInprogress}>
              Cancel
            </Button>

            <Button
              variant="solid"
              color="danger"
              loading={deleteInprogress}
              onClick={() => {
                handleDelete(deleteModalId);
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </React.Fragment>
  );
}



function getFiscalYearCode(dateString) {

  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 1–12

  var fiscalYearStart: any = '';
  if (month >= 4) {
    fiscalYearStart = year;
  }
  else {
    fiscalYearStart = year - 1;
  }
  return fiscalYearStart;
}